export function formatEventDateTime(
  isoString: string,
  timezone: string,
): { formatted: string; abbreviation: string } {
  const date = new Date(isoString);

  const formatted = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    timeZoneName: 'short',
  }).format(date);

  // Extract timezone abbreviation (e.g. "CDT", "EST")
  const abbreviation =
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';

  return { formatted, abbreviation };
}

function partsIn(
  date: Date,
  timezone: string,
  options: Intl.DateTimeFormatOptions,
): Record<string, string> {
  const parts: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-US', {
    ...options,
    timeZone: timezone,
  }).formatToParts(date)) {
    parts[p.type] = p.value;
  }
  return parts;
}

function yearIn(date: Date, timezone: string): string {
  return partsIn(date, timezone, { year: 'numeric' }).year ?? '';
}

/** "Nov 1", or "Nov 1, 2027" when the year differs from `now`'s (in the event timezone). */
export function formatDeadlineDate(
  iso: string,
  timezone: string,
  now: Date = new Date(),
): string {
  const date = new Date(iso);
  const p = partsIn(date, timezone, { month: 'short', day: 'numeric' });
  const year = yearIn(date, timezone);
  const yearSuffix = year !== yearIn(now, timezone) ? `, ${year}` : '';
  return `${p.month} ${p.day}${yearSuffix}`;
}

/**
 * "Sun, Nov 1 at 11:59 PM MST" in the event timezone. The year is inserted
 * after the day only when it differs from `now`'s year in that timezone.
 */
export function formatDeadline(
  iso: string,
  timezone: string,
  now: Date = new Date(),
): string {
  const p = partsIn(new Date(iso), timezone, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  const date = formatDeadlineDate(iso, timezone, now);
  return `${p.weekday}, ${date} at ${p.hour}:${p.minute} ${p.dayPeriod} ${p.timeZoneName}`;
}
